export const colors = {
    /**
     * Brand
     *
     * Canonical brand color:
     * #402172
     *
     * The lighter shades are intentionally kept violet,
     * not pink/magenta.
     */
    brand: {
        50: '#F5F2FA',
        100: '#EBE5F4',
        200: '#D8CCE9',
        300: '#BAA5D7',
        400: '#8A72C2',
        500: '#402172',

        // Main brand color
        600: '#402172',

        700: '#351B60',
        800: '#2B164F',
        900: '#21103D',
        950: '#140923',
    },

    /**
     * Neutral palette
     */
    neutral: {
        0: '#FFFFFF',

        50: '#F8FAFC',
        100: '#F1F5F9',
        200: '#E2E8F0',
        300: '#CBD5E1',
        400: '#94A3B8',
        500: '#64748B',
        600: '#475569',
        700: '#334155',
        800: '#1E293B',
        900: '#0F172A',
        950: '#080D14',

        1000: '#000000',
    },

    /**
     * Status colors
     */
    status: {
        success: {
            subtle: '#ECFDF3',
            muted: '#BBF7D0',

            main: '#22C55E',
            strong: '#16A34A',
            foreground: '#15803D',

            darkSubtle: '#102A1A',
            darkMuted: '#164327',
            darkForeground: '#4ADE80',
        },

        warning: {
            subtle: '#FFF8E6',
            muted: '#FDE7A8',

            main: '#F59E0B',
            strong: '#D97706',
            foreground: '#FBBF24',

            darkSubtle: '#2D2209',
            darkMuted: '#4D3807',
            darkForeground: '#FBBF24',
        },

        error: {
            subtle: '#FFF1F2',
            muted: '#FECDD3',

            main: '#EF4444',
            strong: '#DC2626',
            foreground: '#F87171',

            darkSubtle: '#301416',
            darkMuted: '#502022',
            darkForeground: '#F87171',

            translucentBackground: 'rgba(239, 68, 68, 0.25)',
        },

        info: {
            subtle: '#EFF8FF',
            muted: '#BAE6FD',

            main: '#0EA5E9',
            strong: '#0284C7',
            foreground: '#38BDF8',

            darkSubtle: '#102532',
            darkMuted: '#12394D',
            darkForeground: '#38BDF8',
        },
    },

    /**
     * Light theme semantic colors
     */
    light: {
        background: {
            default: '#F6F8FC',
            paper: '#FFFFFF',

            light: '#F1F4F9',
            extraLight: '#F8FAFC',
            tab: '#EEF2F7',
        },

        surface: {
            subtle: '#F8FAFC',
            soft: '#F1F4F9',
            elevated: '#FFFFFF',

            hover: '#F1F5F9',
            selected: '#F0ECF7',
            active: '#E8E1F2',
        },

        text: {
            primary: '#172033',
            secondary: '#536078',
            light: '#8490A4',
            disabled: '#A7B0BF',
        },

        border: {
            light: '#EDF0F5',
            main: '#DCE2EA',
            strong: '#C8D0DC',
            error: '#FECACA',
        },

        chat: {
            primary: '#F1F4F9',
            secondary: '#E8EDF5',
            button: '#DEE5EF',
        },

        notification: {
            primary: '#F2F5FA',
            secondary: '#E7ECF4',
        },
    },

    /**
     * Dark theme
     *
     * Intentionally neutral/cool.
     * Brand purple is NOT mixed into the surfaces.
     */
    dark: {
        background: {
            default: '#0D0E12',
            paper: '#15161B',

            light: '#191A20',
            extraLight: '#1D1E24',
            tab: '#17181D',
        },

        surface: {
            subtle: '#121318',
            soft: '#15161B',
            elevated: '#1D1E24',

            hover: '#1B1C22',
            selected: '#22232B',
            active: '#282A33',
        },

        text: {
            primary: '#F1F5F9',
            secondary: '#A6B0C3',
            light: '#737F95',
            disabled: '#566176',
        },

        border: {
            light: 'rgba(255, 255, 255, 0.045)',
            main: 'rgba(255, 255, 255, 0.071)',
            strong: 'rgba(255, 255, 255, 0.12)',
            error: '#7F1D2D',
        },

        chat: {
            primary: '#131419',
            secondary: '#191A20',
            button: '#202128',
        },

        notification: {
            primary: '#17181D',
            secondary: '#1D1E24',
        },
    },

    /**
     * Brand gradients
     */
    gradients: {
        primaryButton:
            'linear-gradient(135deg, #694DA7 0%, #402172 100%)',

        primaryButtonHover:
            'linear-gradient(135deg, #795DB5 0%, #4C2A80 100%)',

        primaryButtonSelected:
            'linear-gradient(135deg, #55388E 0%, #351B60 100%)',
    },

    /**
     * Alpha colors
     */
    alpha: {
        white: {
            4: 'rgba(255, 255, 255, 0.04)',
            6: 'rgba(255, 255, 255, 0.06)',
            8: 'rgba(255, 255, 255, 0.08)',
            12: 'rgba(255, 255, 255, 0.12)',
            16: 'rgba(255, 255, 255, 0.16)',
        },

        black: {
            4: 'rgba(0, 0, 0, 0.04)',
            6: 'rgba(0, 0, 0, 0.06)',
            8: 'rgba(0, 0, 0, 0.08)',
            12: 'rgba(0, 0, 0, 0.12)',
            16: 'rgba(0, 0, 0, 0.16)',
        },

        brand: {
            4: 'rgba(64, 33, 114, 0.04)',
            6: 'rgba(64, 33, 114, 0.06)',
            8: 'rgba(64, 33, 114, 0.08)',
            12: 'rgba(64, 33, 114, 0.12)',
            16: 'rgba(64, 33, 114, 0.16)',
            20: 'rgba(64, 33, 114, 0.20)',
            24: 'rgba(64, 33, 114, 0.24)',
        },

        /**
         * Uses brand[400] instead of #402172.
         * Better visibility on dark surfaces.
         */
        brandDark: {
            4: 'rgba(138, 114, 194, 0.04)',
            6: 'rgba(138, 114, 194, 0.06)',
            8: 'rgba(138, 114, 194, 0.08)',
            12: 'rgba(138, 114, 194, 0.12)',
            16: 'rgba(138, 114, 194, 0.16)',
            20: 'rgba(138, 114, 194, 0.20)',
            24: 'rgba(138, 114, 194, 0.24)',
        },

        /**
         * Mode-agnostic translucent status backgrounds.
         * Used by shared status surfaces (badges, bullets, overlays).
         */
        status: {
            success: 'rgba(34, 197, 94, 0.20)',
            warning: 'rgba(245, 158, 11, 0.20)',
            error: 'rgba(239, 68, 68, 0.20)',
        },
    },
} as const;